import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button } from '@mui/material';
import { Stack } from '@mui/system';
import {
  usePostOperatorsFilesMutation,
  usePostOperatorsVehiclesMutation,
  usePutOperatorsVehiclesMutation,
} from 'api/queries/operators';
import { CarrierDialog, VerticalStepper } from 'remote/global';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FormStep } from 'utils/data/data-types';
import { runSequentially } from 'utils/helpers';
import { VehicleDtoSchema } from '../BookingFormFactory/types';
import { VehicleDefaultLocationFormFields } from './VehicleDefaultLocationForm';
import { VehicleDetailsFormFields } from './VehicleDetailsForm';
import { VehicleDocumentsFormFields } from './VehicleDocumentsForm';
import { VehicleExtrasFormFields } from './VehicleExtrasForm';
import { VehicleImageFormFields } from './VehicleImageForm';
import {
  VehicleDefaultLocationSchemaType,
  VehicleDetailsSchemaType,
  VehicleDocumentsSchemaType,
  VehicleExtrasSchemaType,
  VehicleImageSchemaType,
  vehicleDefaultLocationSchema,
  vehicleDetailsSchema,
  vehicleDocumentsSchema,
  vehicleExtrasSchema,
  vehicleImageSchema,
} from './types';

type VehicleCreateFormProps = {
  steps: FormStep[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  onComplete: () => void;
};

type FormResult = [
  VehicleDetailsSchemaType?,
  VehicleExtrasSchemaType?,
  VehicleImageSchemaType?,
  VehicleDocumentsSchemaType?,
  VehicleDefaultLocationSchemaType?
];

function VehicleDetailsStepForm({
  defaultValues,
  onValid,
}: {
  defaultValues?: VehicleDetailsSchemaType;
  onValid: (data: VehicleDetailsSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleDetailsSchemaType>({
    defaultValues: {
      lengthMeters: '' as any, // Makes sure that field is controlled
      licensePlate: '',
      make: '',
      mileage: '' as any,
      model: '',
      seats: '' as any,
      standingPlaces: '' as any,
      year: '' as any,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleDetailsSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleDetailsFormFields form={formInstance} focusInitial />
      <Button
        type='submit'
        variant='contained'
        disabled={isSubmitting || isValidating || isLoading}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('continue')}
      </Button>
    </form>
  );
}

function VehicleExtrasStepForm({
  defaultValues,
  onValid,
  onBack,
}: {
  defaultValues?: VehicleExtrasSchemaType;
  onValid: (data: VehicleExtrasSchemaType) => void;
  onBack: (data: VehicleExtrasSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleExtrasSchemaType>({
    defaultValues: {
      availableForOffer: undefined,
      kidFriendly: true,
      acceptInternationalRides: true,
      extras: [],
      ...defaultValues,
    },
    resolver: zodResolver(vehicleExtrasSchema),
  });
  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleExtrasFormFields form={formInstance} focusInitial />
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

function VehicleImagesStepForm({
  defaultValues,
  onValid,
  onBack,
  isMarkedAsActive,
}: {
  defaultValues?: VehicleImageSchemaType;
  onValid: (data: VehicleImageSchemaType) => void;
  onBack: (data: VehicleImageSchemaType) => void;
  isMarkedAsActive: boolean;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleImageSchemaType>({
    defaultValues: {
      attachments: undefined,
      ...defaultValues,
      isMarkedAsActive,
    },
    resolver: zodResolver(vehicleImageSchema),
  });

  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleImageFormFields form={formInstance} />
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
          autoFocus
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

function VehicleDocumentsStepForm({
  defaultValues,
  onValid,
  onBack,
}: {
  defaultValues?: VehicleDocumentsSchemaType;
  onValid: (data: VehicleDocumentsSchemaType) => void;
  onBack: (data: VehicleDocumentsSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleDocumentsSchemaType>({
    defaultValues: {
      insuranceDocuments: undefined,
      vehicleCards: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleDocumentsSchema),
  });
  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleDocumentsFormFields form={formInstance} />
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
          autoFocus
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

function VehicleDefaultLocationStepForm({
  defaultValues,
  onValid,
  onBack,
}: {
  defaultValues?: VehicleDefaultLocationSchemaType;
  onValid: (data: VehicleDefaultLocationSchemaType) => void;
  onBack: (data: VehicleDefaultLocationSchemaType) => void;
}) {
  const { t } = useTranslation();
  const formInstance = useForm<VehicleDefaultLocationSchemaType>({
    defaultValues: {
      defaultLocation: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleDefaultLocationSchema),
  });
  const {
    getValues,
    handleSubmit,
    formState: { isSubmitting, isLoading },
  } = formInstance;

  // isValidating gets stuck on true for some reason, don't use it
  const isFormDisabled = isSubmitting || isLoading;

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleDefaultLocationFormFields form={formInstance} focusInitial />
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isFormDisabled}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isFormDisabled}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
        >
          {t('confirm')}
        </Button>
      </Stack>
    </form>
  );
}

function VehicleCreateForm({
  steps,
  activeStep,
  setActiveStep,
  onComplete,
}: VehicleCreateFormProps) {
  const { t } = useTranslation();
  const [formResult, setFormResult] = useState<FormResult>();
  const [
    vehicleDetailsValues,
    vehicleExtrasValues,
    vehicleImagesValues,
    vehicleDocumentValues,
    vehicleLocationValues,
  ] = formResult ?? [];
  const postOperatorsVehiclesMutation = usePostOperatorsVehiclesMutation();
  const postOperatorsFilesMutation = usePostOperatorsFilesMutation();
  const putOperatorsVehiclesMutation = usePutOperatorsVehiclesMutation();

  const setFormResultForStep = (step: number, data: any) => {
    setFormResult((prevState) => {
      const newState = prevState ?? [];
      newState[step] = data;
      return newState;
    });
  };

  const handleGoBack = (data: any) => {
    setFormResultForStep(activeStep, data);
    setActiveStep(activeStep - 1);
  };

  const handleValidStep = async (data: any) => {
    const nextStepIndex = activeStep + 1;
    // If is not the last step
    if (nextStepIndex < steps.length) {
      setFormResultForStep(activeStep, data);
      setActiveStep(nextStepIndex);
      return;
    }

    const payload: VehicleDtoSchema = {
      ...vehicleDetailsValues,
      ...vehicleExtrasValues,
      ...vehicleLocationValues,
      ...data,
      // Filter out undefined values
      extras: vehicleExtrasValues?.extras?.filter((v) => typeof v !== 'undefined'),
    };

    const response = await postOperatorsVehiclesMutation.mutateAsync(payload);
    const vehicleId = response.id;

    const files = [
      ...(vehicleImagesValues?.attachments ?? []),
      ...(vehicleDocumentValues?.insuranceDocuments ?? []),
      ...(vehicleDocumentValues?.vehicleCards ?? []),
    ];

    const promises = files.map((file) => () => {
      const formData = new FormData();
      formData.append('file', file.originalFile as File);
      formData.append('fileType', file.fileType);
      formData.append('referenceId', String(vehicleId));
      return postOperatorsFilesMutation.mutateAsync(formData);
    });

    // Call the promise functions sequentially
    await runSequentially(promises);

    if (payload.availableForOffer) {
      // need to make a put request after vehicle with images is created for it to become active
      await putOperatorsVehiclesMutation.mutateAsync({ ...payload, id: vehicleId });
    }

    toast.success(t('Toast.success'));
    onComplete();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        mt: [null, '3rem'],
        mb: ['3rem', '6rem'],
        maxWidth: '1500px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          px: '1rem',
          maxWidth: '400px',
          width: '100%',
        }}
      >
        {activeStep === 0 && (
          <VehicleDetailsStepForm defaultValues={vehicleDetailsValues} onValid={handleValidStep} />
        )}
        {activeStep === 1 && (
          <VehicleExtrasStepForm
            defaultValues={vehicleExtrasValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
        {activeStep === 2 && (
          <VehicleImagesStepForm
            isMarkedAsActive={vehicleExtrasValues?.availableForOffer!}
            defaultValues={vehicleImagesValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
        {activeStep === 3 && (
          <VehicleDocumentsStepForm
            defaultValues={vehicleDocumentValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
        {activeStep === 4 && (
          <VehicleDefaultLocationStepForm
            defaultValues={vehicleLocationValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
      </Box>
    </Box>
  );
}
interface Props {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function VehicleCreateModalForm({ isModalOpen, setIsModalOpen }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation();

  const handleClose = () => {
    setIsModalOpen(false);
    setActiveStep(0);
  };

  const steps: FormStep[] = [
    {
      label: t('modals.createVehicle.steps.vehicleDetails.title'),
      description: '',
    },
    {
      label: t('modals.createVehicle.steps.vehicleExtras.title'),
      description: t('modals.createVehicle.steps.vehicleExtras.subtitle'),
    },
    {
      label: t('modals.createVehicle.steps.vehicleImages.title'),
      description: t('modals.createVehicle.steps.vehicleImages.subtitle'),
    },
    {
      label: t('modals.createVehicle.steps.vehicleDocuments.title'),
      description: t('modals.createVehicle.steps.vehicleDocuments.subtitle'),
    },
    {
      label: t('modals.createVehicle.steps.vehicleLocation.title'),
      description: '',
    },
  ];

  return (
    <CarrierDialog
      open={isModalOpen}
      setOpen={setIsModalOpen}
      onClose={handleClose}
      title={t('modals.createVehicle.title').toString()}
      preventCloseOutside
    >
      <VerticalStepper activeStep={activeStep} steps={steps} />
      <VehicleCreateForm
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        onComplete={handleClose}
      />
    </CarrierDialog>
  );
}

export default VehicleCreateModalForm;
